import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { ClipboardList, Settings, ShieldCheck, BarChart3, ChevronLeft } from "lucide-react";
import { listBousalaAttempts } from "@/lib/bousala/admin.functions";
import { BousalaRecoveryPanel } from "@/components/admin/bousala/recovery-panel";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const attemptsQ = () =>
  queryOptions({
    queryKey: ["admin", "bousala", "attempts", { status: "all", tier: "all", search: "", includeTest: true, page: 1, pageSize: 100 }],
    queryFn: () =>
      listBousalaAttempts({ data: { status: "all", tier: "all", search: "", includeTest: true, page: 1, pageSize: 100 } }),
  });

export const Route = createFileRoute("/_authenticated/admin/bousala/")({
  head: () => ({
    meta: [{ title: "بوصلة د. سارة — مساحة الإدارة" }, { name: "robots", content: "noindex" }],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: BousalaOverviewPage,
});

function BousalaOverviewPage() {
  const attempts = useQuery(attemptsQ());
  const rows = attempts.data?.rows ?? [];
  const total = attempts.data?.total ?? 0;
  const ready = rows.filter((r) => r.status === "report_ready" || r.has_report).length;
  const scored = rows.filter((r) => r.status === "scored" && !r.has_report).length;
  const pending = rows.filter((r) => r.status === "in_progress").length;

  const cards = [
    {
      to: "/admin/bousala/attempts",
      title: "إدارة المحاولات",
      desc: "افتح كل محاولة، اعرف حالتها، ولّد التقرير من نفس المكان.",
      hint: attempts.isLoading ? "جاري التحميل…" : `${total} محاولة · ${ready} تقرير جاهز · ${scored} يحتاج توليد`,
      icon: ClipboardList,
    },
    {
      to: "/admin/bousala/review",
      title: "المراجعة قبل النشر",
      desc: "اعتمد البنود والنصوص قبل جعل الاختبار متاحاً للجمهور.",
      hint: "اعتماد المحتوى",
      icon: ShieldCheck,
    },
    {
      to: "/admin/bousala/analytics",
      title: "التحليلات",
      desc: "تابع الإكمال، الفئات، الإيرادات، وجلسات التفسير.",
      hint: `${pending} محاولة قيد الإجراء`,
      icon: BarChart3,
    },
    {
      to: "/admin/bousala/settings",
      title: "الإعدادات والفحوصات",
      desc: "إدارة التخصصات، فحوصات الجاهزية، وحالة التسعير.",
      hint: "مصدر الحقيقة والتحقق",
      icon: Settings,
    },
  ] as const;

  return (
    <div className="space-y-6" dir="rtl">
      <header>
        <p className="kicker text-modern-orange">مساحة بوصلة</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold">إدارة بوصلة د. سارة</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          هذه الصفحة تجمع المحاولات، المعاينات، الفحوصات، والنشر في مسار واحد واضح بدون تحويلات مفاجئة إلى البورتال.
        </p>
      </header>

      <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <li key={card.to}>
              <Link to={card.to} className="group flex h-full flex-col justify-between rounded-2xl bg-card p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-lift">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-2xl bg-primary/20 text-foreground">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h2 className="font-display text-lg font-extrabold">{card.title}</h2>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{card.desc}</p>
                </div>
                <div className="mt-5 flex items-center justify-between gap-3">
                  <span className="text-[11px] font-bold text-muted-foreground">{card.hint}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-primary transition-transform group-hover:-translate-x-0.5">
                    فتح اللوحة
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      <BousalaRecoveryPanel />
    </div>
  );
}